// src/mobile/MobileArticlePage.js
// Mobile Article detail page with optimized reading experience

import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import { ArrowLeft, Clock, AlertCircle } from 'lucide-react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import MobileNavbar from './MobileNavbar';
import MobileResourceCard from './MobileResourceCard';
import { useArticle, useResourcesList } from '../hooks/useContentful';
import './styles/mobile-article.css';

// Format date for display
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

// Rich text rendering options for mobile
const richTextOptions = (assetMap = {}) => ({
  renderNode: {
    [BLOCKS.HEADING_2]: (node, children) => <h2>{children}</h2>,
    [BLOCKS.HEADING_3]: (node, children) => <h3>{children}</h3>,
    [BLOCKS.HEADING_4]: (node, children) => <h4>{children}</h4>,
    [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
    [BLOCKS.UL_LIST]: (node, children) => <ul>{children}</ul>,
    [BLOCKS.OL_LIST]: (node, children) => <ol>{children}</ol>,
    [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,
    [BLOCKS.QUOTE]: (node, children) => <blockquote>{children}</blockquote>,
    [BLOCKS.HR]: () => <hr />,
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const assetId = node.data?.target?.sys?.id;
      const asset = assetMap[assetId];
      if (!asset) return null;
      return (
        <figure>
          <img
            src={asset.url}
            alt={asset.description || asset.title || ''}
            loading="lazy"
          />
          {asset.description && <figcaption>{asset.description}</figcaption>}
        </figure>
      );
    },
    [INLINES.HYPERLINK]: (node, children) => (
      <a
        href={node.data.uri}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
  },
});

const MobileArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  const { data: article, loading, error } = useArticle(slug);
  const { data: allResources } = useResourcesList();

  // Get related articles (same category, excluding current)
  const relatedArticles = React.useMemo(() => {
    if (!article || !allResources?.resources) return [];
    const articleTopicNames = article.topics?.map(t => t?.name).filter(Boolean) || [];
    return allResources.resources
      .filter(
        (r) =>
          r.slug !== slug &&
          r.type === 'article' &&
          (r.category === article.category ||
            r.topics?.some((t) => articleTopicNames.includes(t?.name)))
      )
      .slice(0, 2);
  }, [article, allResources, slug]);

  // Build asset map for rich text
  const assetMap = React.useMemo(() => {
    const assets = article?.body?.links?.assets?.block;
    if (!assets) return {};
    return assets.reduce((map, asset) => {
      map[asset.sys?.id] = asset;
      return map;
    }, {});
  }, [article]);

  // Handle back navigation
  const handleBack = () => {
    navigate('/resources');
  };

  // Render loading skeleton
  const renderSkeleton = () => (
    <div className="mobile-article__loading">
      <div className="mobile-article__skeleton-back" />
      <div className="mobile-article__skeleton-badge" />
      <div className="mobile-article__skeleton-title" />
      <div className="mobile-article__skeleton-title" />
      <div className="mobile-article__skeleton-summary" />
      <div className="mobile-article__skeleton-summary" />
      <div className="mobile-article__skeleton-author">
        <div className="mobile-article__skeleton-avatar" />
        <div className="mobile-article__skeleton-meta">
          <div className="mobile-article__skeleton-name" />
          <div className="mobile-article__skeleton-role" />
        </div>
      </div>
      <div className="mobile-article__skeleton-image" />
      <div className="mobile-article__skeleton-body">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="mobile-article__skeleton-paragraph" />
        ))}
      </div>
    </div>
  );

  // Render error state
  if (error) {
    return (
      <div className="mobile-article">
        <MobileNavbar />
        <div className="mobile-article__error">
          <div className="mobile-article__error-icon">
            <AlertCircle size={28} />
          </div>
          <h1 className="mobile-article__error-title">Article Not Found</h1>
          <p className="mobile-article__error-text">
            We couldn't find the article you're looking for. It may have been moved or deleted.
          </p>
          <Link to="/resources" className="mobile-article__error-btn">
            <ArrowLeft size={18} />
            Back to Resources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-article">
      {/* Progress Bar */}
      <motion.div className="mobile-article__progress" style={{ scaleX }} />

      <MobileNavbar />

      {loading ? (
        renderSkeleton()
      ) : article ? (
        <>
          {/* Header */}
          <header className="mobile-article__header">
            <button className="mobile-article__back" onClick={handleBack}>
              <ArrowLeft size={18} />
              Back to Resources
            </button>

            {article.category && (
              <span className="mobile-article__badge">{article.category}</span>
            )}

            <h1 className="mobile-article__title">{article.title}</h1>

            {article.summary && (
              <p className="mobile-article__summary">{article.summary}</p>
            )}

            {/* Author Strip */}
            <div className="mobile-article__author-strip">
              {article.author?.avatarUrl && (
                <img
                  src={article.author.avatarUrl}
                  alt={article.author.name}
                  className="mobile-article__author-avatar"
                />
              )}
              <div className="mobile-article__author-info">
                <p className="mobile-article__author-name">
                  {article.author?.name || 'Rosalytics Team'}
                </p>
                {article.author?.role && (
                  <p className="mobile-article__author-role">
                    {article.author.role}
                  </p>
                )}
              </div>
              <div className="mobile-article__meta">
                <span className="mobile-article__date">
                  {formatDate(article.publishedAt)}
                </span>
                {article.readingTime && (
                  <span className="mobile-article__reading-time">
                    <Clock size={12} />
                    {article.readingTime}
                  </span>
                )}
              </div>
            </div>
          </header>

          {/* Hero Image */}
          {article.heroImage?.url && (
            <div className="mobile-article__hero">
              <img
                src={article.heroImage.url}
                alt={article.heroImage.alt || article.title}
                className="mobile-article__hero-image"
              />
            </div>
          )}

          {/* Article Body */}
          {article.body?.json?.content && (
            <article className="mobile-article__body">
              <div className="mobile-rich-text">
                {documentToReactComponents(article.body.json, richTextOptions(assetMap))}
              </div>
            </article>
          )}

          {/* Tags */}
          {article.topics?.length > 0 && (
            <div className="mobile-article__tags">
              {article.topics.map((topic) => (
                <span key={topic.id || topic.name} className="mobile-article__tag">
                  {topic.name}
                </span>
              ))}
            </div>
          )}

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mobile-article__related">
              <h2 className="mobile-article__related-title">Related Articles</h2>
              <div className="mobile-article__related-list">
                {relatedArticles.map((resource, index) => (
                  <MobileResourceCard
                    key={resource.slug}
                    resource={resource}
                    variant="compact"
                    index={index}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      ) : null}
    </div>
  );
};

export default MobileArticlePage;
